import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Users, ArrowLeft, Ticket, Heart } from "lucide-react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { PhotoGallery } from "@/components/PhotoGallery";
import { ShareButton, useSeo } from "@/components/Share";

export default function EventDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [qty, setQty] = useState(1);
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/events/${slug}`).then((r) => setEvent(r.data)).catch(() => nav("/events"));
  }, [slug, nav]);
  useSeo({ item: event, kind: "events", slug, title: event?.title || "", description: event?.short_description || event?.description, image: event?.image });

  const book = async () => {
    if (!event.price_lookup_key) {
      toast("This is a free event — just turn up.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: event.price_lookup_key,
        quantity: qty,
        origin_url: window.location.origin,
        item_type: "event",
        item_id: event.id,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Couldn't start checkout. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const toggleFav = async () => {
    if (!user) return nav("/login");
    try {
      await api.post("/favorites", { item_type: "event", item_id: event.id });
      toast.success("Saved to your favourites");
    } catch { toast.error("Couldn't save"); }
  };

  if (!event) return <div className="max-w-6xl mx-auto p-20 text-center text-[color:var(--forest-2)]">Loading…</div>;
  const isSeeLink = event.price_mode === "see_link";
  const isFree = !isSeeLink && (event.price_mode === "free" || event.price_from === 0);

  const start = new Date(event.start_date);

  return (
    <div>
      <div className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <img src={event.image} alt={event.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-scrim" />
        <button onClick={() => nav(-1)} className="absolute top-6 left-6 btn-pill btn-outline !border-white/40 !text-white bg-black/20 backdrop-blur">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="absolute bottom-8 left-0 right-0 px-6 lg:px-10">
          <div className="max-w-[1200px] mx-auto text-white">
            <div className="chip !bg-white/15 !text-white !border-white/30 backdrop-blur mb-3">{event.category}</div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="font-display text-4xl md:text-6xl leading-tight tracking-tight max-w-3xl">
              {event.title}
            </motion.h1>
            <p className="mt-4 max-w-xl opacity-90">{event.short_description}</p>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="grid grid-cols-3 gap-4 pb-8 border-b border-[color:var(--forest-line)]">
            <div>
              <div className="text-[0.7rem] uppercase tracking-[0.2em] text-[color:var(--forest-2)] mb-2">When</div>
              <div className="font-display text-xl">{start.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</div>
              <div className="text-sm text-[color:var(--forest-2)]">{start.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}{event.recurrence && event.recurrence !== "none" ? ` · repeats ${event.recurrence}` : ""}</div>
            </div>
            <div>
              <div className="text-[0.7rem] uppercase tracking-[0.2em] text-[color:var(--forest-2)] mb-2">Where</div>
              <div className="font-display text-xl">{event.venue}</div>
              <div className="text-sm text-[color:var(--forest-2)]">{event.suburb}</div>
            </div>
            <div>
              <div className="text-[0.7rem] uppercase tracking-[0.2em] text-[color:var(--forest-2)] mb-2">Capacity</div>
              <div className="font-display text-xl">{event.capacity.toLocaleString()}</div>
              <div className="text-sm text-[color:var(--forest-2)]">seats</div>
            </div>
          </div>
          <div className="prose-heygc mt-10">
            <p>{event.description}</p>
          </div>
          {event.tags?.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2">
              {event.tags.map((t) => <span key={t} className="chip">{t}</span>)}
            </div>
          )}
          <PhotoGallery images={event.gallery} title={event.title} />
        </div>

        <aside className="lg:col-span-5">
          <div className="sticky top-28 bg-white rounded-3xl border border-[color:var(--forest-line)] p-8 shadow-[0_20px_60px_-30px_rgba(11,37,36,0.2)]">
            <div className="text-[0.7rem] uppercase tracking-[0.2em] text-[color:var(--forest-2)] mb-2">{isSeeLink ? "Tickets" : "Tickets from"}</div>
            <div className="flex items-baseline gap-2">
              {isSeeLink ? (
                <span className="font-display text-2xl leading-snug" data-testid="event-price-see-link">See link for ticket price</span>
              ) : (
                <>
                  <span className="font-display text-5xl">{isFree ? "Free" : `$${event.price_from}`}</span>
                  {!isFree && <span className="text-[color:var(--forest-2)]">AUD</span>}
                </>
              )}
            </div>

            {!isSeeLink && !isFree && !event.booking_url && (
              <div className="mt-6 flex items-center justify-between border border-[color:var(--forest-line)] rounded-full px-4 py-2">
                <span className="text-sm">Quantity</span>
                <div className="flex items-center gap-3">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-7 h-7 rounded-full border">−</button>
                  <span className="min-w-4 text-center font-medium">{qty}</span>
                  <button onClick={() => setQty(Math.min(10, qty + 1))} className="w-7 h-7 rounded-full border">+</button>
                </div>
              </div>
            )}

            {event.booking_url ? (
              <a
                href={event.booking_url}
                target="_blank"
                rel="noreferrer"
                data-testid={TID.eventBookBtn}
                className="mt-6 btn-pill btn-primary w-full text-base py-4 justify-center"
              >
                <Ticket size={16} /> {isSeeLink ? "See ticket prices & book" : "Book / find out more"}
              </a>
            ) : (
            <button
              onClick={book}
              disabled={busy || isSeeLink}
              data-testid={TID.eventBookBtn}
              className="mt-6 btn-pill btn-primary w-full text-base py-4 disabled:opacity-50"
            >
              <Ticket size={16} /> {isSeeLink ? "Ticket link coming soon" : isFree ? "RSVP" : busy ? "Redirecting…" : "Book tickets"}
            </button>
            )}
            <button onClick={toggleFav} data-testid={TID.favBtn(event.id)} className="mt-3 btn-pill btn-outline w-full py-3">
              <Heart size={15} /> Save
            </button>
            <ShareButton kind="events" slug={event.slug} title={event.title} className="mt-3 w-full py-3 justify-center" />
            <div className="mt-6 pt-6 border-t border-[color:var(--forest-line)] text-sm text-[color:var(--forest-2)] space-y-2">
              <div className="flex items-center gap-2"><Calendar size={13} /> {start.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })}</div>
              <div className="flex items-center gap-2"><MapPin size={13} /> {event.address}</div>
              <div className="flex items-center gap-2"><Users size={13} /> Presented by {event.organizer}</div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
